import {
  type ComponentType,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Radio,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type {
  ProcessedFormField,
  ScopeBundle,
} from "@/types/api/formSchema";
import { useFormulaFields } from "@/hooks/useFormulaFields";
import {
  buildConditionalFieldNames,
  buildDefaultValues,
  buildFieldRules,
} from "../utils/schemaUtils";
import {
  FULL_WIDTH_NAMES,
  SECTION_ORDER,
  THIRD_WIDTH_NAMES,
  groupBranchFields,
} from "../utils/branchSections";
import { usePersistedFormData } from "../hooks/usePersistedFormData";
import { HydrationContext } from "../hooks/HydrationContext";
import {
  GST_ADDRESS_SOURCE_FIELD,
  GST_PINCODE_SOURCE_FIELD,
  useGstAddressAutofill,
} from "../hooks/useGstAddressAutofill";
import {
  CONFIRM_ACCOUNT_FIELD,
  useAccountConfirmReset,
} from "../hooks/useAccountConfirmReset";
import { useAccountTypes } from "../hooks/useAccountTypes";
import { stateFromGstin } from "../utils/gstinStateCodes";
import { fetchIfsc, isLikelyIfsc } from "@/services/razorpayIfsc";
import {
  fetchPincodeLocation,
  isLikelyPincode,
} from "@/services/pincodeLookup";
import FieldGroup from "./FieldGroup";
import { BankVerificationEffect } from "./BankVerificationEffect";
import { resolveTooltip } from "../utils/fieldTooltips";
import SubSectionHeading from "./SubSectionHeading";
import { TextField } from "../fields/TextField";
import { TextareaField } from "../fields/TextareaField";
import { SelectField } from "../fields/SelectField";
import { RadioField } from "../fields/RadioField";
import { CheckboxField } from "../fields/CheckboxField";
import { BooleanField } from "../fields/BooleanField";
import { EmailsField } from "../fields/EmailsField";
import { DateField } from "../fields/DateField";
import { PhoneField } from "../fields/PhoneField";
import {
  AttachmentField,
  AttachmentOwnerContext,
} from "../fields/AttachmentField";
import type { SchemaRendererHandle } from "./SchemaRenderer";

/* eslint-disable @typescript-eslint/no-explicit-any */
const FIELD_RENDERERS: Record<string, ComponentType<any>> = {
  text: TextField,
  email: TextField,
  emails: EmailsField,
  link: TextField,
  textarea: TextareaField,
  select: SelectField,
  radio: RadioField,
  checkbox: CheckboxField,
  boolean: BooleanField,
  date: DateField,
  phone: PhoneField,
  attachment: AttachmentField,
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const FULL_WIDTH_TYPES = new Set([
  "radio",
  "checkbox",
  "boolean",
  "textarea",
  "attachment",
]);

// Brand blue is theme.palette.primary.main — use "primary.main" in MUI sx.
// PRIMARY_TINT is a paler blue used for hover/selected-primary card backgrounds;
// no matching theme token yet, so kept as a local constant.
const PRIMARY_TINT = "#F5F7FF";

type BranchRow = Record<string, unknown>;
type FormValues = { branches: BranchRow[] };

type Props = {
  branchFields: ProcessedFormField[];
  onSubmit: (branches: BranchRow[]) => void | Promise<void>;
  initialValues?: BranchRow[];
  scope?: ScopeBundle;
  attachmentOwner?: { ownerRef?: string; ownerType?: string };
  persistKey?: string | null;
};

function withAccountNumberConfirm(
  fields: ProcessedFormField[],
): ProcessedFormField[] {
  const idx = fields.findIndex((f) => f.name === "facility_account_number");
  if (idx === -1) return fields;
  const synthetic: ProcessedFormField = {
    name: CONFIRM_ACCOUNT_FIELD,
    type: "text",
    display_name: "Re-enter Account Number",
    resolvedLabel: "Re-enter Account Number",
    required: "business_details.vendor_type != 'International'",
    hidden: "business_details.vendor_type == 'International'",
    noPaste: true,
  };
  const out = [...fields];
  out.splice(idx + 1, 0, synthetic);
  return out;
}

function stripSyntheticFromBranches(branches: BranchRow[]): BranchRow[] {
  return branches.map((b) => {
    const { [CONFIRM_ACCOUNT_FIELD]: _confirm, ...rest } = b as Record<
      string,
      unknown
    >;
    void _confirm;
    return rest as BranchRow;
  });
}

function makeEmptyBranch(
  branchFields: ProcessedFormField[],
  isPrimary: boolean,
): BranchRow {
  const defaults = buildDefaultValues(branchFields);
  defaults["is_primary_facility"] = isPrimary;
  return defaults;
}

export const BranchesRepeater = forwardRef<SchemaRendererHandle, Props>(
  function BranchesRepeater(
    { branchFields, onSubmit, initialValues, scope, attachmentOwner, persistKey },
    ref,
  ) {
    const branchFieldsWithSynthetic = useMemo(
      () => withAccountNumberConfirm(branchFields),
      [branchFields],
    );

    const initialBranches = useMemo<BranchRow[]>(() => {
      if (initialValues && initialValues.length > 0) {
        // Confirm field is stripped before save — reseed on rehydrate.
        return initialValues.map((b) => {
          const acc = (b as Record<string, unknown>)["facility_account_number"];
          if (typeof acc === "string" && acc.length > 0) {
            return { ...b, [CONFIRM_ACCOUNT_FIELD]: acc };
          }
          return b;
        });
      }
      return [makeEmptyBranch(branchFields, true)];
    }, [branchFields, initialValues]);

    const form = useForm<FormValues>({
      defaultValues: { branches: initialBranches },
      mode: "onBlur",
    });

    const hydrationCount = usePersistedFormData(persistKey ?? null, form);

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: "branches",
    });

    const [rowErrors, setRowErrorsState] = useState<Record<number, string>>({});
    const [sectionError, setSectionErrorState] = useState<string | null>(null);

    const handlePrimarySelect = (idx: number) => {
      const current = form.getValues("branches") ?? [];
      current.forEach((_, i) => {
        form.setValue(`branches.${i}.is_primary_facility`, i === idx, {
          shouldDirty: true,
        });
      });
    };

    const handleAddBranch = () => {
      append(makeEmptyBranch(branchFields, false));
    };

    const handleRemove = (idx: number) => {
      const current = form.getValues("branches") ?? [];
      const wasPrimary = current[idx]?.is_primary_facility === true;
      remove(idx);
      if (wasPrimary) {
        // Derive the post-remove shape locally rather than re-reading RHF —
        // avoids any dependency on when useFieldArray's remove() flushes.
        const remaining = current.filter((_, i) => i !== idx);
        if (
          remaining.length > 0 &&
          !remaining.some((b) => b?.is_primary_facility === true)
        ) {
          form.setValue("branches.0.is_primary_facility", true, {
            shouldDirty: true,
          });
        }
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        form.clearErrors();
        void form.handleSubmit((data) =>
          onSubmit(stripSyntheticFromBranches(data.branches)),
        )();
      },
      reset: () => {
        form.reset();
        setRowErrorsState({});
        setSectionErrorState(null);
      },
      getValues: () => form.getValues() as unknown as Record<string, unknown>,
      isDirty: () => form.formState.isDirty,
      markSaved: () => {
        form.reset(form.getValues(), {
          keepValues: true,
          keepDirty: false,
          keepDirtyValues: false,
        });
      },
      setFieldErrors: (errors) => {
        for (const [name, message] of Object.entries(errors)) {
          form.setError(name as `branches.${number}.${string}`, {
            type: "server",
            message,
          });
        }
      },
      setRowErrors: (errors) => setRowErrorsState(errors),
      setSectionError: (message) => setSectionErrorState(message),
    }));

    return (
      <FormProvider {...form}>
        <HydrationContext.Provider value={hydrationCount}>
        <AttachmentOwnerContext.Provider value={attachmentOwner ?? {}}>
        <form
          onSubmit={form.handleSubmit((data) =>
            onSubmit(stripSyntheticFromBranches(data.branches)),
          )}
        >
          {sectionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sectionError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {fields.map((branch, idx) => (
              <BranchCard
                key={branch.id}
                index={idx}
                branchFields={branchFieldsWithSynthetic}
                scope={scope}
                onPrimarySelect={() => handlePrimarySelect(idx)}
                canRemove={fields.length > 1}
                onRemove={() => handleRemove(idx)}
                rowError={rowErrors[idx]}
              />
            ))}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddBranch}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: "primary.main", bgcolor: PRIMARY_TINT },
              }}
            >
              Add Another Branch
            </Button>
          </Box>
        </form>
        </AttachmentOwnerContext.Provider>
        </HydrationContext.Provider>
      </FormProvider>
    );
  },
);

type BranchCardProps = {
  index: number;
  branchFields: ProcessedFormField[];
  scope?: ScopeBundle;
  onPrimarySelect: () => void;
  canRemove: boolean;
  onRemove: () => void;
  rowError?: string;
};

function BranchCard({
  index,
  branchFields,
  scope,
  onPrimarySelect,
  canRemove,
  onRemove,
  rowError,
}: BranchCardProps) {
  const { control, watch, setValue, getValues } =
    useFormContext<FormValues>();
  const accountTypesQuery = useAccountTypes();
  const isPrimary = watch(`branches.${index}.is_primary_facility`) === true;
  // Bank penny-drop success writes `bank_verification_status: "verified"`
  // (see BankVerificationEffect). While it holds, `account_holder_name` must
  // stay locked to the bank-reported name — a vendor overriding a verified
  // value defeats the whole penny-drop cross-check (see QET-515).
  const bankVerified =
    watch(`branches.${index}.bank_verification_status`) === "verified";
  const gstLine1Filled =
    watch(`branches.${index}.${GST_ADDRESS_SOURCE_FIELD}`) === "gstin";
  const gstPincodeFilled =
    watch(`branches.${index}.${GST_PINCODE_SOURCE_FIELD}`) === "gstin";
  const branchValues = (watch(`branches.${index}`) ?? {}) as Record<
    string,
    unknown
  >;
  const { visibleFields } = useFormulaFields(
    branchFields,
    scope,
    branchValues,
  );
  const grouped = useMemo(
    () => groupBranchFields(visibleFields),
    [visibleFields],
  );

  // Hidden branch-level fields must not retain values (persist / re-enable leak).
  const conditionalFieldNames = useMemo(
    () => buildConditionalFieldNames(branchFields),
    [branchFields],
  );
  const visibleFieldNames = useMemo(
    () => new Set(visibleFields.map((f) => f.name)),
    [visibleFields],
  );
  useEffect(() => {
    for (const name of conditionalFieldNames) {
      if (visibleFieldNames.has(name)) continue;
      const path = `branches.${index}.${name}` as const;
      const current = getValues(path);
      const isEmpty =
        current == null ||
        current === "" ||
        (Array.isArray(current) && current.length === 0);
      if (!isEmpty) {
        setValue(path, undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }
  }, [
    conditionalFieldNames,
    visibleFieldNames,
    index,
    getValues,
    setValue,
  ]);

  useGstAddressAutofill(index);
  const gstinValue = watch(`branches.${index}.gstin_primary`) as
    | string
    | undefined;
  useEffect(() => {
    const derived = stateFromGstin(gstinValue);
    if (!derived) return;
    const current = getValues(`branches.${index}.state_of_registration`);
    if (current !== derived) {
      setValue(`branches.${index}.state_of_registration`, derived, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [gstinValue, index, setValue, getValues]);

  const ifscValue = watch(`branches.${index}.facility_ifsc_code`) as
    | string
    | undefined;
  const lastResolvedIfsc = useRef<string | null>(null);
  useEffect(() => {
    if (!ifscValue) return;
    const code = ifscValue.trim().toUpperCase();
    if (!isLikelyIfsc(code)) return;
    if (lastResolvedIfsc.current === code) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const data = await fetchIfsc(code);
      if (cancelled || !data) return;
      lastResolvedIfsc.current = code;
      setValue(`branches.${index}.facility_bank_name`, data.BANK ?? "", {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`branches.${index}.facility_bank_branch`, data.BRANCH ?? "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ifscValue, index, setValue]);

  useAccountConfirmReset(index);

  const pincodeValue = watch(`branches.${index}.facility_pincode`) as
    | string
    | undefined;
  const lastResolvedPincode = useRef<string | null>(null);
  const [pincodeResolved, setPincodeResolved] = useState(false);
  useEffect(() => {
    if (!pincodeValue) {
      lastResolvedPincode.current = null;
      setPincodeResolved(false);
      return;
    }
    const code = String(pincodeValue).trim();
    if (!isLikelyPincode(code)) return;
    if (lastResolvedPincode.current === code) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const data = await fetchPincodeLocation(code);
      if (cancelled || !data) return;
      lastResolvedPincode.current = code;
      setValue(`branches.${index}.facility_city`, data.city, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`branches.${index}.facility_country`, data.country_code, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setPincodeResolved(true);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pincodeValue, index, setValue]);

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: isPrimary ? "primary.main" : "#E0E4EF",
        borderRadius: "10px",
        bgcolor: isPrimary ? PRIMARY_TINT : "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: isPrimary ? "rgba(54,85,242,0.15)" : "divider",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          Branch #{index + 1}
          {isPrimary && (
            <Box
              component="span"
              sx={{ color: "primary.main", fontWeight: 600, ml: 1 }}
            >
              · Primary
            </Box>
          )}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: isPrimary ? "primary.main" : "text.secondary",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Radio
              checked={isPrimary}
              onChange={onPrimarySelect}
              size="small"
              sx={{
                p: 0.5,
                color: "primary.main",
                "&.Mui-checked": { color: "primary.main" },
              }}
            />
            Primary Facility
          </Box>
          {canRemove && (
            <IconButton
              type="button"
              size="small"
              onClick={onRemove}
              aria-label={`Remove branch ${index + 1}`}
              sx={{ color: "text.secondary" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        {rowError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {rowError}
          </Alert>
        )}
        {SECTION_ORDER.map((section) => {
          const sectionFields = grouped[section];
          if (!sectionFields || sectionFields.length === 0) return null;
          return (
            <Box key={section}>
              {section !== "top" && (
                <SubSectionHeading>{section}</SubSectionHeading>
              )}
              <Grid container columnSpacing={3} rowSpacing={2.5}>
                {sectionFields.map((rawField) => {
                  let schemaField = rawField;
                  if (
                    pincodeResolved &&
                    (schemaField.name === "facility_city" ||
                      schemaField.name === "facility_country")
                  ) {
                    schemaField = { ...schemaField, disabled: true };
                  }
                  const filledByGstin =
                    (schemaField.name === "facility_address_line_1" &&
                      gstLine1Filled) ||
                    (schemaField.name === "facility_pincode" &&
                      gstPincodeFilled);
                  if (
                    schemaField.name === "facility_account_type" &&
                    accountTypesQuery.data &&
                    accountTypesQuery.data.length > 0
                  ) {
                    schemaField = {
                      ...schemaField,
                      list: accountTypesQuery.data.map((o) => ({
                        name: o.value,
                        display_name: o.label,
                      })),
                    };
                  }
                  if (schemaField.name === CONFIRM_ACCOUNT_FIELD) {
                    const primary = branchValues.facility_account_number;
                    const hasPrimary =
                      typeof primary === "string" && primary.trim().length > 0;
                    if (!hasPrimary) {
                      schemaField = { ...schemaField, disabled: true };
                    }
                  }
                  // Lock the account-holder name to the bank-reported value
                  // once penny-drop confirms it (QET-515). Clearing
                  // account_number / IFSC re-enables the field —
                  // BankVerificationEffect flips bank_verification_status
                  // back to unset on pair change.
                  const lockedByPennyDrop =
                    schemaField.name === "account_holder_name" && bankVerified;
                  if (lockedByPennyDrop) {
                    schemaField = { ...schemaField, disabled: true };
                  }
                  const helperText = lockedByPennyDrop
                    ? "Auto-filled from bank penny-drop; edit account number or IFSC to change."
                    : filledByGstin
                      ? "Auto-filled from the GSTIN's registered address. Edit if needed."
                      : schemaField.helperText;
                  const Renderer =
                    FIELD_RENDERERS[schemaField.type] || TextField;
                  const isFullWidth =
                    FULL_WIDTH_TYPES.has(schemaField.type) ||
                    FULL_WIDTH_NAMES.has(schemaField.name);
                  const isThirdWidth = THIRD_WIDTH_NAMES.has(schemaField.name);
                  const mdWidth = isFullWidth ? 12 : isThirdWidth ? 4 : 6;
                  const rhfName =
                    `branches.${index}.${schemaField.name}` as const;

                  return (
                    <Grid
                      item
                      xs={12}
                      md={mdWidth}
                      key={schemaField.name}
                      data-field-name={rhfName}
                    >
                      <Controller
                        control={control}
                        name={rhfName}
                        rules={
                          schemaField.name === CONFIRM_ACCOUNT_FIELD
                            ? {
                                required: "Please re-enter the account number.",
                                validate: (value: unknown) => {
                                  const original = getValues(
                                    `branches.${index}.facility_account_number`,
                                  );
                                  return (
                                    value === original ||
                                    "Account numbers don't match."
                                  );
                                },
                              }
                            : buildFieldRules(schemaField)
                        }
                        render={({ field: rhfField }) => (
                          <FieldGroup
                            name={rhfField.name}
                            label={schemaField.resolvedLabel}
                            required={!!schemaField.required}
                            helperText={helperText}
                            tooltip={resolveTooltip(schemaField)}
                            hideLabel={schemaField.type === "boolean"}
                          >
                            <Renderer
                              field={schemaField}
                              value={rhfField.value}
                              name={rhfField.name}
                              onChange={rhfField.onChange}
                              onBlur={rhfField.onBlur}
                            />
                          </FieldGroup>
                        )}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {section === "BANK DETAILS" && (
                <BankVerificationEffect branchIndex={index} />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
